import React, { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, TrendingDown, Sliders, Link2, Check } from 'lucide-react';

// Fixed cost inputs (industry defaults; documented on the /for-merchants "The Math" section)
const AVG_DISPUTE_COST_USD = 37.5;    // midpoint of $25 to $50 ops labor per dispute
const KYPA_VERIFY_COST_USD = 0.10;
const CARDHOLDERS = 1_000_000;
const OG_IMAGE_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api/og/simulator';

function fmtCurrency(n) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function Slider({ label, unit, value, min, max, step, onChange, testId, hint }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-xs font-bold tracking-widest text-slate-300 uppercase" htmlFor={testId}>{label}</label>
        <span className="text-sm font-mono text-emerald-300" data-testid={`${testId}-value`}>{value}{unit}</span>
      </div>
      <input
        id={testId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-emerald-400 cursor-pointer"
        data-testid={testId}
      />
      <div className="text-[10px] text-slate-500 mt-1">{hint}</div>
    </div>
  );
}

export default function SlashRecoverySimulator() {
  const location = useLocation();
  const initial = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      a: Number(params.get('a')) || 25,
      x: Number(params.get('x')) || 12,
      e: Number(params.get('e')) || 10,
    };
  }, [location.search]);

  const [activationPct, setActivationPct] = useState(initial.a);
  const [actionsPerUser, setActionsPerUser] = useState(initial.x);
  const [errorRateBps, setErrorRateBps] = useState(initial.e);
  const [copied, setCopied] = useState(false);

  const numbers = useMemo(() => {
    const activeUsers = CARDHOLDERS * (activationPct / 100);
    const annualActions = activeUsers * actionsPerUser;
    const errorRate = errorRateBps / 10000;
    const exceptions = annualActions * errorRate;
    const marginDestroyed = exceptions * AVG_DISPUTE_COST_USD;
    const verifySpend = annualActions * KYPA_VERIFY_COST_USD;
    const netDelta = marginDestroyed - verifySpend;
    return { activeUsers, annualActions, exceptions, marginDestroyed, verifySpend, netDelta };
  }, [activationPct, actionsPerUser, errorRateBps]);

  const positive = numbers.netDelta > 0;
  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}?a=${activationPct}&x=${actionsPerUser}&e=${errorRateBps}#simulator`
    : '';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (Number(params.get('a')) !== activationPct || Number(params.get('x')) !== actionsPerUser || Number(params.get('e')) !== errorRateBps) {
      params.set('a', String(activationPct));
      params.set('x', String(actionsPerUser));
      params.set('e', String(errorRateBps));
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
    }
  }, [activationPct, actionsPerUser, errorRateBps]);

  const handleShare = async () => {
    if (typeof navigator === 'undefined') return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch (_e) {}
  };

  const linkedInShareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/api/og/share/simulator?a=${activationPct}&x=${actionsPerUser}&e=${errorRateBps}`
    : '';
  const handleLinkedInPost = () => {
    if (typeof window === 'undefined') return;
    const dialog = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(linkedInShareUrl)}`;
    window.open(dialog, '_blank', 'noopener,noreferrer,width=650,height=650');
  };

  const handleXPost = () => {
    if (typeof window === 'undefined') return;
    const text = `${shareTitle}: ${shareDesc}`;
    const dialog = `https://twitter.com/intent/tweet?url=${encodeURIComponent(linkedInShareUrl)}&text=${encodeURIComponent(text)}`;
    window.open(dialog, '_blank', 'noopener,noreferrer,width=650,height=650');
  };

  const shareTitle = `Kojiru KYPA prevention math: ${positive ? fmtCurrency(numbers.netDelta) : 'break-even'} per 1M cardholders (illustrative)`;
  const shareDesc = `${Math.round(numbers.exceptions).toLocaleString()} out-of-scope agent actions/yr refused before payment, each with signed evidence. ${fmtCurrency(numbers.marginDestroyed)} dispute ops cost avoided vs. ${fmtCurrency(numbers.verifySpend)} in KYPA verify spend. Illustrative, not a guarantee. Compute your own numbers.`;
  const ogImage = `${OG_IMAGE_BASE}?a=${activationPct}&x=${actionsPerUser}&e=${errorRateBps}`;

  return (
    <div id="simulator" className="border border-white/10 rounded-2xl p-6 bg-slate-950/60" data-testid="slash-recovery-simulator">
      <Helmet>
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={shareTitle} />
        <meta name="twitter:description" content={shareDesc} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <div className="flex items-start justify-between mb-1 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sliders className="w-4 h-4 text-cyan-400" />
            <div className="text-[10px] font-bold tracking-[2px] text-cyan-300">PREVENTION AND EVIDENCE SIMULATOR</div>
          </div>
          <h3 className="text-xl sm:text-2xl font-black text-white mb-1">Type your own numbers. See what prevention is worth.</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleShare}
            className={`inline-flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-md border transition-colors ${copied ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300' : 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20'}`}
            data-testid="sim-share-button"
            title="Copy the shareable URL that encodes these exact slider values"
          >
            {copied ? <><Check className="w-3.5 h-3.5" /> Link copied</> : <><Link2 className="w-3.5 h-3.5" /> Copy link</>}
          </button>
          <button
            type="button"
            onClick={handleLinkedInPost}
            className="inline-flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-md border transition-colors border-[#0A66C2]/60 bg-[#0A66C2]/10 text-[#4B9EFF] hover:bg-[#0A66C2]/20"
            data-testid="sim-linkedin-share"
            title="Open LinkedIn share dialog with this ROI card pre-attached"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden="true"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>
            Post to LinkedIn
          </button>
          <button
            type="button"
            onClick={handleXPost}
            className="inline-flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-md border transition-colors border-white/30 bg-white/5 text-white hover:bg-white/10"
            data-testid="sim-x-share"
            title="Open X (Twitter) share dialog with this ROI card pre-attached"
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden="true"><path d="M18.244 2H21.5l-7.51 8.59L22.75 22h-6.914l-5.415-7.084L4.15 22H.892l8.03-9.187L1.25 2h7.09l4.895 6.475L18.244 2zm-1.213 18h1.914L7.06 4H5.03l12.001 16z"/></svg>
            Post on X
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-400 mb-6">Constants: 1&nbsp;million cardholders, ${AVG_DISPUTE_COST_USD.toFixed(2)} avg ops cost per dispute, ${KYPA_VERIFY_COST_USD.toFixed(2)} per KYPA verify call. Sliders encode into the URL, so the recipient lands on your exact numbers. Illustrative only. No recovery from any pledge is assumed.</p>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <Slider label="Activation rate" unit="%" value={activationPct} min={1} max={100} step={1}
          onChange={setActivationPct} testId="sim-activation" hint="Share of cardholders using agent-mediated payments" />
        <Slider label="Actions / user / yr" unit="" value={actionsPerUser} min={1} max={100} step={1}
          onChange={setActionsPerUser} testId="sim-actions" hint="Agent-mediated transactions per active user per year" />
        <Slider label="Error rate" unit=" bps" value={errorRateBps} min={1} max={200} step={1}
          onChange={setErrorRateBps} testId="sim-error-rate" hint={`Out-of-scope agent-transaction rate (${(errorRateBps / 100).toFixed(2)}%)`} />
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <div className="border border-white/10 rounded-lg p-3 bg-black/40">
          <div className="text-[9px] font-bold tracking-widest text-slate-500 mb-1">ANNUAL ACTIONS</div>
          <div className="text-lg font-mono font-bold text-white" data-testid="sim-actions-total">{(numbers.annualActions / 1_000_000).toFixed(2)}M</div>
        </div>
        <div className="border border-red-500/25 rounded-lg p-3 bg-red-500/[0.05]">
          <div className="text-[9px] font-bold tracking-widest text-red-300 mb-1">REFUSED WITH EVIDENCE</div>
          <div className="text-lg font-mono font-bold text-red-200" data-testid="sim-exceptions">{Math.round(numbers.exceptions).toLocaleString()}</div>
        </div>
        <div className="border border-red-500/25 rounded-lg p-3 bg-red-500/[0.05]">
          <div className="text-[9px] font-bold tracking-widest text-red-300 mb-1">DISPUTE COST AVOIDED</div>
          <div className="text-lg font-mono font-bold text-red-200" data-testid="sim-margin-destroyed">{fmtCurrency(numbers.marginDestroyed)}</div>
        </div>
        <div className="border border-emerald-500/25 rounded-lg p-3 bg-emerald-500/[0.05]">
          <div className="text-[9px] font-bold tracking-widest text-emerald-300 mb-1">KYPA VERIFY SPEND</div>
          <div className="text-lg font-mono font-bold text-emerald-200" data-testid="sim-verify-spend">{fmtCurrency(numbers.verifySpend)}</div>
        </div>
      </div>

      <div className={`rounded-xl p-5 border-2 ${positive ? 'border-emerald-500 bg-emerald-500/[0.06]' : 'border-amber-500 bg-amber-500/[0.06]'}`} data-testid="sim-verdict">
        <div className="flex items-center gap-2 mb-2">
          {positive ? <TrendingUp className="w-5 h-5 text-emerald-400" /> : <TrendingDown className="w-5 h-5 text-amber-400" />}
          <div className={`text-[10px] font-bold tracking-widest ${positive ? 'text-emerald-300' : 'text-amber-300'}`}>NET PREVENTION ECONOMICS PER 1M CARDHOLDERS</div>
        </div>
        <div className={`text-3xl font-black font-mono ${positive ? 'text-emerald-300' : 'text-amber-300'}`} data-testid="sim-net-delta">
          {positive ? '+' : '−'}{fmtCurrency(Math.abs(numbers.netDelta))}
        </div>
        <p className="text-xs text-slate-400 leading-relaxed mt-2">
          {positive
            ? `At these inputs, refusing out-of-scope actions before payment beats verify spend by ${fmtCurrency(numbers.netDelta)} per year. Each refusal leaves a signed evidence record. No pledge recovery is assumed. Illustrative, not a guarantee.`
            : `At these inputs, verify spend exceeds the dispute cost avoided. This usually happens with very low activation. Each refusal still leaves a signed evidence record. No pledge recovery is assumed.`}
        </p>
      </div>
    </div>
  );
}
